import { useEffect, useState, memo, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, ButtonType, ButtonSize, IconName } from '@hills-design-system/components';
import { CmsFile } from '../../../types/contentstack';
import ChevronRightIcon from '../../../components/icons/ChevronRightIcon';
import { useTranslation } from '../../../hooks/useTranslation';
import { TranslationKeyEnum } from '../../../types/constants';
import { getMenuByType } from '../../../services/contentstack';

export interface CmsResourceItem {
  title: string;
  description?: string;
  icon?: CmsFile;
  url?: string;
  external?: boolean;
}

export interface CmsResourcesSectionData {
  name?: string;
  component?: string;
  title: string;
  description: string;
  resources: CmsResourceItem[];
  viewAllLink?: { label: string; url: string };
}

interface CmsResourcesSectionProps {
  data?: CmsResourcesSectionData;
  className?: string;
}

interface ContentstackResponse {
  title?: string;
  header?: string;
  children?: Array<{
    link: {
      link_label: string;
      link_url?: string;
      external: boolean;
      aria_label?: string;
      link_image?: {
        url: string;
        title?: string;
      };
    };
  }>;
}

export const CmsResourcesSection: React.FC<CmsResourcesSectionProps> = ({
  data: initialData,
  className = '',
}) => {
  const [data, setData] = useState<CmsResourcesSectionData | null>(initialData ?? null);
  const [loading, setLoading] = useState(!initialData);
  const [error, setError] = useState<string | null>(null);
  const { t, locale } = useTranslation();
  const navigate = useNavigate();

  useEffect(() => {
    if (initialData) {
      setData(initialData);
      setLoading(false);
      return;
    }

    const fetchResourcesData = async () => {
      try {
        setLoading(true);
        setError(null);
        const normalizedLocale = locale.toLowerCase();
        const resourcesComponent = await getMenuByType<ContentstackResponse>('Resources', normalizedLocale);

        if (!resourcesComponent) {
          throw new Error('No resources data returned');
        }

        const allChildren = resourcesComponent.children || [];
        const sideBarButtonChild = allChildren.find((child) => child.link.aria_label === 'Button');
        const resourceChildren = allChildren.filter((child) => child.link.aria_label !== 'Button');

        const resources: CmsResourceItem[] = resourceChildren.map(
          (child) => ({
            title: child.link.link_label,
            url: child.link.link_url,
            external: child.link.external,
            icon: child.link.link_image?.url
              ? {
                  url: child.link.link_image.url,
                  description: child.link.link_image.title,
                }
              : undefined,
          })
        );

        setData({
          title: resourcesComponent.title || t(TranslationKeyEnum.Resources),
          description: resourcesComponent.header || '',
          resources,
          viewAllLink: sideBarButtonChild?.link.link_url
            ? { label: sideBarButtonChild.link.link_label, url: sideBarButtonChild.link.link_url }
            : undefined,
        });
      } catch (err) {
        console.error('[CmsResourcesSection] Failed to fetch resources:', err);
        setError(err instanceof Error ? err.message : t(TranslationKeyEnum.ErrorLoadingResources));
      } finally {
        setLoading(false);
      }
    };

    fetchResourcesData();
  }, [initialData, locale]);

  // Early returns for loading, error, and empty states
  if (loading) {
    return (
      <div className={`relative w-full ${className}`}>
        <div className="flex items-center justify-center min-h-[200px]">
          <p className="text-gray-600">{t(TranslationKeyEnum.Loading)}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`relative w-full ${className}`}>
        <div className="flex items-center justify-center min-h-[200px] bg-red-50 rounded-lg border border-red-200">
          <p className="text-red-600">{t(TranslationKeyEnum.ErrorLoadingResources)}: {error}</p>
        </div>
      </div>
    );
  }

  if (!data?.resources?.length) {
    return null;
  }

  return (
    <section className={`relative w-full ${className}`}>
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        {/* Header - Left Column */}
        <div className="lg:col-span-3">
          <h2 className="text-xl font-bold text-gray-900 mb-2">
            {data.title}
          </h2>
          <p className="font-bold text-heading-125 leading-heading text-black-600">
            {data.description}
          </p>
          {data.viewAllLink && (
            <Button
              label={data.viewAllLink.label}
              type={ButtonType.Tertiary}
              size={ButtonSize.Small}
              icon={IconName.ArrowRight}
              align="left"
              className="hover:bg-transparent w-fit whitespace-nowrap mt-4"
              onClick={() => navigate(data.viewAllLink!.url)}
            />
          )}
        </div>

        {/* Resources Grid - Right Column */}
        <div className="lg:col-span-9">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {data.resources.map((resource, index) => (
              <ResourceCard key={resource.url || `${resource.title}-${index}`} resource={resource} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

const ResourceCard: React.FC<{ resource: CmsResourceItem }> = memo(({ resource }) => {
  const navigate = useNavigate();

  const handleClick = useCallback(() => {
    if (resource.url) {
      navigate(resource.url);
      window.scrollTo(0, 0);
    }
  }, [navigate, resource.url]);

  const cardContent = useMemo(() => (
    <>
      {resource.icon?.url && (
        <div className="flex-shrink-0 py-[8px] px-[12px] min-h-[109px] w-fit flex items-center justify-center">
          <img
            src={resource.icon.url}
            alt={resource.icon.description || resource.title}
            className="object-contain w-[95px]"
          />
        </div>
      )}
      <div className="flex-1 min-w-0">
        <h3 className="font-medium text-gray-900 text-sm leading-snug">
          {resource.title}
        </h3>
        {resource.description && (
          <p className="font-bold text-heading-125 leading-heading text-black-600">
            {resource.description}
          </p>
        )}
      </div>
      <div className="flex-shrink-0 pr-[12px]">
        <ChevronRightIcon width={20} height={20} className="text-gray-400" />
      </div>
    </>
  ), [resource.icon, resource.title, resource.description]);

  const cardClasses = "flex items-center gap-3 rounded-lg border border-gray-200 transition-all h-full bg-[#EDF2F8] group-hover:bg-blue-100 group-hover:border-blue-200";

  // No URL - static card
  if (!resource.url) {
    return <div className={cardClasses}>{cardContent}</div>;
  }

  // External link - use anchor tag
  if (resource.external) {
    return (
      <a
        href={resource.url}
        target="_blank"
        rel="noopener noreferrer"
        className="block no-underline group cursor-pointer"
        aria-label={`${resource.title} (opens in new tab)`}
      >
        <div className={cardClasses}>{cardContent}</div>
      </a>
    );
  }

  // Internal link - use button with navigate
  return (
    <button
      type="button"
      onClick={handleClick}
      className="block no-underline group cursor-pointer w-full text-left bg-transparent border-none p-0"
      aria-label={resource.title}
    >
      <div className={cardClasses}>{cardContent}</div>
    </button>
  );
});

ResourceCard.displayName = 'ResourceCard';

export default CmsResourcesSection;
